import { useState } from "react";
import { Info } from "lucide-react";
import { MaskLines, FadeUp, Eyebrow } from "@/components/Reveal";
import { Seo } from "@/components/Seo";

const Field = ({ id, label, children }) => (
  <label htmlFor={id} className="block">
    <span className="font-caps text-[11px] tracking-[0.24em] uppercase text-gold-deep">{label}</span>
    {children}
  </label>
);

const inputCls =
  "mt-3 w-full border-0 border-b border-heritage/30 bg-transparent px-0 py-3 font-display text-2xl text-heritage placeholder:text-heritage/35 focus:border-gold focus:outline-none focus:ring-0 transition-colors";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    setDone(true);
  };

  return (
    <>
      <Seo title="Contact" path="/contact" description="Make an enquiry about Little Englishman's Bay Estate in Tobago and its hospitality, conservation and tourism projects." />
      <section className="mx-auto max-w-[1440px] px-5 pb-28 pt-[170px] md:px-10 lg:pt-[220px]" data-testid="contact-page">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Eyebrow>Contact</Eyebrow>
            <MaskLines lines={["Enquiries &", "correspondence."]} delay={0.2} className="mt-6 font-display text-5xl leading-[0.98] text-heritage sm:text-6xl lg:text-7xl" testid="contact-title" />
            <FadeUp delay={0.4}>
              <p className="mt-8 max-w-md text-lg leading-relaxed text-muted-ink">
                We welcome enquiries about Little Englishman's Bay Estate and its projects.
              </p>
              <div className="mt-10 flex gap-4 border border-gold/50 bg-gold/10 p-6" data-testid="contact-coming-soon-notice">
                <Info className="mt-1 shrink-0 text-gold-deep" size={20} />
                <div>
                  <p className="font-display text-2xl text-heritage">Contact details coming soon</p>
                  <p className="mt-2 text-muted-ink">The estate's email address and other contact details will be published here shortly. Until then, this form is a preview and messages are not yet sent.</p>
                </div>
              </div>
            </FadeUp>
          </div>
          <FadeUp delay={0.3} className="lg:col-span-6 lg:col-start-7">
            <form onSubmit={submit} className="space-y-10 border border-gold/30 bg-white/50 p-6 md:p-12" data-testid="contact-form">
              <Field id="name" label="Name">
                <input id="name" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} data-testid="contact-name-input" />
              </Field>
              <Field id="email" label="Email">
                <input id="email" type="email" required value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} data-testid="contact-email-input" />
              </Field>
              <Field id="message" label="Message">
                <textarea id="message" required rows={5} value={form.message} onChange={set("message")} placeholder="Your enquiry" className={`${inputCls} resize-none text-xl`} data-testid="contact-message-input" />
              </Field>
              <button
                type="submit"
                className="group relative w-full overflow-hidden rounded-full bg-heritage px-8 py-5 font-caps text-[12px] tracking-[0.3em] uppercase text-ivory transition-transform duration-300 active:scale-[0.98]"
                data-testid="contact-submit-button"
              >
                <span className="absolute inset-0 origin-left scale-x-0 bg-gold transition-transform duration-500 group-hover:scale-x-100" />
                <span className="relative group-hover:text-heritage-deep">Send Enquiry</span>
              </button>
              {done && (
                <p className="border-l-2 border-gold pl-4 text-heritage" role="status" data-testid="contact-form-result">
                  Thank you{form.name ? `, ${form.name.split(" ")[0]}` : ""}. Online enquiries are not yet active, so your message has not been sent. Contact details will be published on this page soon.
                </p>
              )}
            </form>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
